import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format, addDays, startOfWeek, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { roomsAPI, roomBookingsAPI, locationsAPI, employeesAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { DoorOpen, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { RoomCalendarCard } from './roombooking/RoomCalendarCard';
import { BookingDialog } from './roombooking/BookingDialog';

const initialBookingForm = {
  start_time: '09:00',
  end_time: '10:00',
  title: '',
  description: '',
  is_series: false,
  series_type: 'daily',
  series_end_date: '',
  series_weekdays: [1, 2, 3, 4, 5],
};

export default function RoomBooking() {
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission('employees', 'edit');
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [myEmployee, setMyEmployee] = useState(null);

  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookingForm, setBookingForm] = useState(initialBookingForm);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const weekStart = format(startOfWeek(currentWeek, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const weekEnd = format(addDays(startOfWeek(currentWeek, { weekStartsOn: 1 }), 6), 'yyyy-MM-dd');
      const [roomsRes, locationsRes, bookingsRes, myProfileRes] = await Promise.all([
        roomsAPI.getBookableByEmployee(),
        locationsAPI.getAll(),
        roomBookingsAPI.getAll({ start_date: weekStart, end_date: weekEnd }),
        employeesAPI.getMyProfile().catch(() => ({ data: null })),
      ]);
      setMyEmployee(myProfileRes.data);
      setRooms(roomsRes.data || []);
      setLocations(locationsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [currentWeek]);

  useEffect(() => { loadData(); }, [loadData]);

  const myLocationNames = useMemo(() => {
    if (!myEmployee?.location_id) return [];
    const locationIds = [myEmployee.location_id, ...(myEmployee.additional_location_ids || [])];
    return locations.filter((l) => locationIds.includes(l.id)).map((l) => l.name);
  }, [myEmployee, locations]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentWeek, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentWeek]);

  const isSlotAvailable = useCallback((roomId, date, startTime, endTime) => {
    if (!date) return true;
    const dayStr = format(date, 'yyyy-MM-dd');
    const startDt = `${dayStr}T${startTime}:00`;
    const endDt = `${dayStr}T${endTime}:00`;
    return !bookings.some(
      (b) => b.room_id === roomId && b.start_datetime < endDt && b.end_datetime > startDt
    );
  }, [bookings]);

  const generateSeriesDates = useCallback(() => {
    if (!selectedDate) return [];
    if (!bookingForm.is_series) return [selectedDate];
    const dates = [];
    let cur = selectedDate;
    const endDate = parseISO(bookingForm.series_end_date);
    while (cur <= endDate) {
      const dow = cur.getDay();
      if (bookingForm.series_type === 'daily') {
        if (bookingForm.series_weekdays.includes(dow === 0 ? 7 : dow)) dates.push(new Date(cur));
      } else if (bookingForm.series_type === 'weekly' && dow === selectedDate.getDay()) {
        dates.push(new Date(cur));
      }
      cur = addDays(cur, 1);
    }
    return dates;
  }, [bookingForm, selectedDate]);

  const getSeriesConflicts = useCallback(() => {
    if (!selectedRoom) return [];
    const dates = generateSeriesDates();
    return dates
      .filter((d) => !isSlotAvailable(selectedRoom.id, d, bookingForm.start_time, bookingForm.end_time))
      .map((d) => format(d, 'dd.MM.yyyy'));
  }, [generateSeriesDates, isSlotAvailable, selectedRoom, bookingForm.start_time, bookingForm.end_time]);

  const openBookingDialog = (room, date) => {
    setSelectedRoom(room);
    setSelectedDate(date);
    setBookingForm({
      ...initialBookingForm,
      series_end_date: format(addDays(date, 14), 'yyyy-MM-dd'),
    });
    setBookingDialogOpen(true);
  };

  const handleBookRoom = async () => {
    if (!bookingForm.title.trim()) { toast.error('Bitte geben Sie einen Titel ein'); return; }
    if (bookingForm.start_time >= bookingForm.end_time) { toast.error('Die Endzeit muss nach der Startzeit liegen'); return; }
    if (bookingForm.is_series && !bookingForm.series_end_date) { toast.error('Bitte geben Sie ein Enddatum für die Serie an'); return; }

    const dates = generateSeriesDates();
    if (dates.length === 0) { toast.error('Keine Buchungstermine in der Serie gefunden'); return; }

    const conflicts = getSeriesConflicts();
    if (conflicts.length > 0 && conflicts.length === dates.length) {
      toast.error('Alle Termine sind bereits belegt');
      return;
    }

    setSaving(true);
    try {
      let successCount = 0;
      let skipCount = 0;
      for (const date of dates) {
        if (!isSlotAvailable(selectedRoom.id, date, bookingForm.start_time, bookingForm.end_time)) {
          skipCount++;
          continue;
        }
        const dayStr = format(date, 'yyyy-MM-dd');
        try {
          await roomBookingsAPI.create({
            room_id: selectedRoom.id,
            title: bookingForm.title,
            description: bookingForm.description || null,
            start_datetime: `${dayStr}T${bookingForm.start_time}:00`,
            end_datetime: `${dayStr}T${bookingForm.end_time}:00`,
          });
          successCount++;
        } catch (err) {
          console.error('Error creating booking for', dayStr, err);
          skipCount++;
        }
      }
      if (successCount > 0) {
        toast.success(skipCount > 0
          ? `${successCount} Buchung(en) erstellt, ${skipCount} übersprungen (bereits belegt)`
          : (successCount === 1 ? 'Raum erfolgreich gebucht' : `${successCount} Buchungen erfolgreich erstellt`));
        setBookingDialogOpen(false);
        loadData();
      } else {
        toast.error('Keine Buchungen konnten erstellt werden');
      }
    } catch (error) {
      console.error('Error booking room:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Buchen');
    } finally {
      setSaving(false);
    }
  };

  const navigateWeek = (direction) => setCurrentWeek((prev) => addDays(prev, direction * 7));

  const handleDeleteBooking = async (booking) => {
    if (!window.confirm(`Buchung "${booking.title}" am ${booking.start_datetime.slice(0, 10)} wirklich löschen?`)) return;
    try {
      await roomBookingsAPI.delete(booking.id);
      toast.success('Buchung gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6 p-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <Skeleton key={`skel-${i}`} className="h-48" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="room-booking-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Raumbuchung</h1>
            <p className="text-muted-foreground mt-1">
              {rooms.length} Räume verfügbar
              {myLocationNames.length > 0 && <span className="ml-1">• {myLocationNames.join(', ')}</span>}
            </p>
          </div>
        </div>

        {myEmployee && myLocationNames.length > 0 && (
          <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg border">
            <MapPin className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-medium">Ihr Standort: {myLocationNames.join(', ')}</p>
              <p className="text-xs text-muted-foreground">Sie können nur Räume an Ihrem Standort buchen.</p>
            </div>
          </div>
        )}

        {myEmployee && myLocationNames.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 text-center">
              <MapPin className="h-10 w-10 mx-auto text-amber-500 mb-3" />
              <h3 className="font-medium text-lg">Kein Standort zugewiesen</h3>
              <p className="text-muted-foreground mt-1">
                Sie sind keinem Standort zugewiesen und können daher keine Räume buchen.
                Bitte wenden Sie sich an Ihren Administrator.
              </p>
            </CardContent>
          </Card>
        )}

        {myLocationNames.length > 0 && (
          <>
            <div className="flex items-center justify-between bg-card border rounded-lg p-4">
              <Button variant="ghost" size="icon" onClick={() => navigateWeek(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="text-center">
                <p className="font-semibold">
                  KW{format(currentWeek, 'w')} • {format(weekDays[0], 'dd.MM.')} - {format(weekDays[6], 'dd.MM.yyyy')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentWeek(new Date())}>Heute</Button>
                <Button variant="ghost" size="icon" onClick={() => navigateWeek(1)}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {rooms.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <DoorOpen className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium text-lg">Keine Räume verfügbar</h3>
                  <p className="text-muted-foreground mt-1">
                    An Ihrem Standort sind keine buchbaren Räume vorhanden.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {rooms.map((room) => (
                  <RoomCalendarCard
                    key={room.id}
                    room={room}
                    location={locations.find((l) => l.id === room.location_id)}
                    weekDays={weekDays}
                    bookings={bookings}
                    onBook={openBookingDialog}
                    currentUserId={user?.id}
                    canManage={canManage}
                    onDelete={handleDeleteBooking}
                  />
                ))}
              </div>
            )}

            {rooms.length > 0 && (
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-2">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded bg-primary/15 border border-primary/40" />
                  Eigene Buchung
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded bg-amber-100 border border-amber-300/50" />
                  Andere Buchung
                </span>
                <span>Klick in eine leere Zelle erstellt eine neue Buchung.</span>
              </div>
            )}
          </>
        )}

        <BookingDialog
          open={bookingDialogOpen}
          onOpenChange={setBookingDialogOpen}
          selectedRoom={selectedRoom}
          selectedDate={selectedDate}
          bookingForm={bookingForm}
          setBookingForm={setBookingForm}
          saving={saving}
          onBook={handleBookRoom}
          isSlotAvailable={isSlotAvailable}
          generateSeriesDates={generateSeriesDates}
          getSeriesConflicts={getSeriesConflicts}
        />
      </div>
    </DashboardLayout>
  );
}
